/*!
 * stem-split-lane.js — the ONE browser client of the server stem-split lane
 * (Demucs on RunPod: config/paid-tools.php → stem_split, StemSplitHandler).
 *
 *   var L = StemSplitLane.create({ infoUrl, runUrl, mode, purpose });
 *   L.info([{ files }])                   → Promise<info>   (price shape, balance, day allowances, poll ceiling;
 *                                           with files [{k, n: name, b: bytes}] also `owned`: {k: {state, job_id}} —
 *                                           which of them this reader already has split or splitting)
 *   L.quote(seconds[, info])              → tokens a file of that length costs THIS reader (the server's formula)
 *   L.split(file, { fallbackReason, acceptPaid, onlyIfOwned, name, signal, onPhase(phase, data) })
 *                                         → Promise<job>    job.stems = [{stem, url, bytes, format, …}]
 *   L.fetchStems(job.stems, names, { signal, onProgress(loaded, total) })
 *                                         → Promise<{ name: ArrayBuffer }>
 *
 * Every refusal is an Error with `.kind` — the server's own `error_kind` where it
 * sent one (insufficient, free_exhausted, gpu_unavailable,
 * split_failed, source_not_audio, …), else login_required / session_expired /
 * network / timeout / gone / expired / file_lost / cancelled — and `.data`, the
 * server's body (balance, needed, store_url, cap, left …).
 *
 * WHY THIS FILE EXISTS. The lane has three browser consumers and had three
 * clients: /stem-splitter's page controller (stem-split-paid.js), an inline copy
 * in /channel-converter, and — the third, which would have been /mashup-maker's —
 * this one instead. The inline copy shows what a copy costs: it polled a fixed
 * 200 × 3 s = 600 s while a job may legitimately wait 1200 s for a fresh GPU host
 * to pull the image (config: runpod.poll_max_s), so a cold start told the buyer
 * «failed» about a split that then succeeded AND charged; and it read the
 * duplicate guard's 409 («this file is already split / already splitting») as a
 * failure, although that answer carries the job to attach to for nothing.
 *
 * What the lane promises and this client keeps:
 *  · the poll ceiling comes from the server (`client_poll_s`), never a constant;
 *  · a 409 duplicate is an ANSWER: attach to the running job, or take the stems
 *    already delivered — no second charge, no second GPU run; and a page can ask
 *    BEFORE the press (info `owned`), so it never names a price for stems the
 *    person already has — `onlyIfOwned` then makes that «free» binding: if the
 *    twin went meanwhile, the server refuses (not_owned) instead of charging;
 *  · the price a page shows is `quote()`, the same formula StemSplitManager::cost
 *    charges, including the reader's own multiplier (Pro discount);
 *  · the upload goes through FileBytes.asFile — a phone that revoked the picked
 *    file's handle gets «file_lost», not a «Failed to fetch» nobody can act on;
 *  · cancelling stops the WAITING, not the work: the server has no cancel, the
 *    split finishes, lands in «Мои генерации», and re-submitting the same file
 *    attaches to it (409) — callers say so instead of promising a refund.
 */
(function (root) {
    'use strict';

    var POLL_MS = 3000;
    var DEFAULT_POLL_S = 1500;       // only when an old server did not publish client_poll_s
    // Silence watchdogs. A phone on a bad network does not FAIL a request — it stops getting bytes and
    // the promise waits forever: «downloading the stems: 8 of 30 MB» frozen on screen (a local stand
    // whose PHP pool refused connections showed exactly that, 24.09.2026). Each wait below is bounded
    // by SILENCE, not by total time, so a slow but moving transfer is never cut.
    var STEM_STALL_MS = 45000;       // no byte of a stem for this long → that stem is asked again
    var STEM_TRIES = 3;
    var STATUS_REQ_MS = 30000;       // one status request; a hung one is a missed poll, the loop goes on
    var UPLOAD_STALL_MS = 90000;     // no upload progress while the body is still going out
    var ANSWER_MS = 180000;          // body sent, no answer (the server only probes and queues)

    function noop() {}
    function typed(kind, msg, data) { var e = new Error(msg || kind); e.kind = kind; e.data = data || null; return e; }
    function cancelled() { var e = new Error('cancelled'); e.name = 'AbortError'; e.kind = 'cancelled'; return e; }
    function csrf() { var m = root.document && root.document.querySelector('meta[name="csrf-token"]'); return m ? m.getAttribute('content') || '' : ''; }
    function withParam(url, k, v) { return url + (url.indexOf('?') === -1 ? '?' : '&') + encodeURIComponent(k) + '=' + encodeURIComponent(v); }
    /** An abort that follows the caller's signal and can also be pulled by a watchdog of ours. */
    function child(signal) {
        var c = new AbortController();
        var on = function () { try { c.abort(); } catch (_) {} };
        if (signal) { if (signal.aborted) on(); else signal.addEventListener('abort', on, { once: true }); }
        c.release = function () { if (signal) signal.removeEventListener('abort', on); };
        return c;
    }
    function sleep(ms, signal) {
        return new Promise(function (res, rej) {
            if (signal && signal.aborted) { rej(cancelled()); return; }
            var t = setTimeout(function () { if (signal) signal.removeEventListener('abort', on); res(); }, ms);
            function on() { clearTimeout(t); rej(cancelled()); }
            if (signal) signal.addEventListener('abort', on, { once: true });
        });
    }

    /** 502/504, or a 503 without our error_kind: the front end could not reach PHP — nothing was decided. */
    function gateway(http, d) { return http === 502 || http === 504 || (http === 503 && !(d && d.error_kind)); }

    /** HTTP status + body → the refusal a page can name. */
    function refusal(http, d) {
        var k = d && d.error_kind;
        if (http === 401) return typed('login_required', 'login required', d);
        if (http === 419) return typed('session_expired', 'csrf token expired', d);
        if (http === 409) return typed(k || 'conflict', 'conflict', d);   // a 409 duplicate never gets here: it is an answer
        if (http === 402) return typed(k || 'insufficient', 'payment required', d);
        if (http === 403) return typed(k || 'disabled', 'lane closed', d);
        if (http === 413) return typed('source_too_large', 'file too large', d);
        if (http === 422) return typed(k || 'invalid', 'file refused', d);
        if (http === 429) return typed(k || 'daily_cap', 'daily limit', d);
        if (http === 503) return typed(k || 'gpu_unavailable', 'lane busy', d);
        // a 5xx is the SERVER failing, not the person's connection — the page must not send them to check their internet
        if (http >= 500) return typed('server_error', 'http ' + http, d);
        return typed('network', 'http ' + http, d);
    }

    /** The song goes AHEAD, in slices sized by the link (root.__uploadBody with `slices` →
     *  /api/upload-slice), and the lane request only names it: one long POST of a 40-MB song is the
     *  request a phone's uplink loses (RU nginx, 14 days to 07.10.2026: 32 % of 20–60-second lane
     *  uploads cut by the client, 75 % of 60–120-second ones). A gateway retry below reuses the
     *  upload; a server that forgot it (410 upload_gone) gets the file again, once.
     *  copyMaxBytes:1 — durable() has already made the page's own copy. */
    function post(url, fd, o) {
        var B = root.__uploadBody;
        if (typeof B !== 'function') return postXhr(url, fd, o);
        var send = function (fresh) {
            return B(fd, { slices: true, copyMaxBytes: 1, signal: o.signal, fresh: fresh, onProgress: o.onUpload }).then(function (body) {
                return postXhr(url, body, o);
            }, function (e) {
                if ((e && e.name === 'AbortError') || (o.signal && o.signal.aborted)) throw cancelled();
                if (typeof B.kind === 'function' && B.kind(e) === 'file') throw typed('file_lost', 'FILE_LOST: ' + String((e && e.message) || e).slice(0, 120));
                throw typed('network', 'upload failed', { slices: true, status: (e && e.status) || 0 });
            });
        };
        return send(false).then(function (r) {
            return (r.http === 410 && r.d && r.d.error_kind === 'upload_gone') ? send(true) : r;
        });
    }

    /** POST with upload progress (fetch has none, and a 40-MB song on a phone is a minute of silence without it). */
    function postXhr(url, fd, o) {
        return new Promise(function (res, rej) {
            var x = new XMLHttpRequest(), settled = false, last = Date.now(), sent = false;
            var stallMs = o.uploadStallMs || UPLOAD_STALL_MS, answerMs = o.answerMs || ANSWER_MS;
            var wd = setInterval(function () {
                if (Date.now() - last > (sent ? answerMs : stallMs)) {
                    // the named refusal FIRST: abort() fires the abort event synchronously, and its
                    // handler would otherwise settle this as «cancelled» — a stall the person did not ask for
                    done(rej, typed('network', sent ? 'no answer after upload' : 'upload stalled', { stalled: true, sent: sent }));
                    try { x.abort(); } catch (_) {}
                }
            }, Math.min(5000, Math.max(5, Math.floor(stallMs / 4))));
            function done(fn, v) { if (settled) return; settled = true; clearInterval(wd); fn(v); }
            x.open('POST', url, true);
            x.withCredentials = true;
            x.setRequestHeader('X-CSRF-TOKEN', csrf());
            x.setRequestHeader('Accept', 'application/json');
            x.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            if (x.upload) {
                x.upload.onprogress = function (e) { last = Date.now(); if (e.lengthComputable && o.onUpload) o.onUpload(e.loaded, e.total); };
                x.upload.onload = function () { sent = true; last = Date.now(); };
            }
            x.onload = function () {
                var d = null;
                try { d = JSON.parse(x.responseText); } catch (_) {}
                done(res, { http: x.status, d: d || {} });
            };
            x.onerror = function () { done(rej, typed('network', 'upload failed')); };
            x.onabort = function () { done(rej, cancelled()); };
            if (o.signal) {
                if (o.signal.aborted) { done(rej, cancelled()); return; }
                o.signal.addEventListener('abort', function () { try { x.abort(); } catch (_) {} done(rej, cancelled()); }, { once: true });
            }
            x.send(fd);
        });
    }

    function durable(file, name) {
        var FB = root.FileBytes;
        if (!FB || typeof FB.asFile !== 'function' || !(file instanceof root.File)) return Promise.resolve(file);
        return FB.asFile(file, name || file.name).catch(function (e) {
            throw typed('file_lost', 'FILE_LOST: ' + String((e && e.message) || e).slice(0, 120));
        });
    }

    function create(cfg) {
        cfg = cfg || {};
        var purpose = cfg.purpose || 'stem_split';
        var pollMs = cfg.pollMs > 0 ? cfg.pollMs : POLL_MS;       // tests shorten it; the server polls RunPod every 3 s
        var stemStallMs = cfg.stemStallMs > 0 ? cfg.stemStallMs : STEM_STALL_MS;
        var statusReqMs = cfg.statusReqMs > 0 ? cfg.statusReqMs : STATUS_REQ_MS;
        var lastInfo = null;

        function info(o) {
            var url = purpose === 'stem_split' ? cfg.infoUrl : withParam(cfg.infoUrl, 'purpose', purpose);
            if (o && o.files && o.files.length) {
                // the same identity submit() matches on: the name and byte count of what will be uploaded, in this mode
                url = withParam(url, 'files', JSON.stringify(o.files));
                if (cfg.mode) url = withParam(url, 'mode', cfg.mode);
            }
            return fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
                .then(function (r) { return r.ok ? r.json() : { enabled: false, http: r.status }; })
                .then(function (d) { lastInfo = d || { enabled: false }; return lastInfo; });
        }

        /**
         * StemSplitManager::cost, on the page: max(floor, base + per_min × ⌈minutes⌉) × the reader's
         * multiplier, rounded up, never under the minimum charge. The server measures the length
         * with ffprobe and the page with its decoder; the two can straddle a minute by a few
         * hundredths of a second (decoder delay, padding), so the page counts a quarter-second
         * more — the button may name one minute more than charged on a file that ends right at
         * a minute, never less. The multiplication is the server's own float arithmetic (the
         * factor arrives as the same double), so a Pro price rounds up exactly as it will there.
         */
        function quote(seconds, inf) {
            var p = (inf || lastInfo || {}).price;
            if (!p || !(p.base >= 0)) return null;
            var mins = seconds > 0 ? Math.ceil((seconds + 0.25) / 60) : 1;
            var price = Math.max(p.floor || 0, p.base + p.per_min * mins);
            var mult = typeof p.mult === 'number' && p.mult > 0 ? p.mult : 1;
            return Math.max(p.min || 1, Math.ceil(price * mult));
        }

        async function poll(job, inf, signal, phase) {
            var budget = ((inf && inf.client_poll_s) || DEFAULT_POLL_S) * 1000, start = Date.now(), misses = 0;
            for (;;) {
                if (signal && signal.aborted) throw cancelled();
                var st = null, http = 0, rc = child(signal), rt = setTimeout(function () { try { rc.abort(); } catch (_) {} }, statusReqMs);
                try {
                    var r = await fetch(job.statusUrl, { credentials: 'same-origin', cache: 'no-store', signal: rc.signal,
                        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
                    http = r.status;
                    st = await r.json().catch(function () { return null; });
                } catch (e) {
                    if (signal && signal.aborted) throw cancelled();
                } finally { clearTimeout(rt); rc.release(); }
                if (http === 404) throw typed('gone', 'job not found', { jobId: job.jobId });
                if (http === 401) throw typed('login_required', 'session ended', { jobId: job.jobId });
                if (st && st.status === 'done') {
                    if (st.expired) throw typed('expired', 'stems already removed', st);
                    return st;
                }
                if (st && st.status === 'error') throw typed(st.error_kind || 'split_failed', st.error_msg || 'split failed', st);
                if (st && st.status) { misses = 0; phase(st.status === 'processing' ? 'processing' : 'queued', { waitedMs: Date.now() - start, jobId: job.jobId }); }
                else phase('poll_miss', { misses: ++misses, http: http });
                if (Date.now() - start > budget) throw typed('timeout', 'no result within ' + Math.round(budget / 1000) + ' s', { jobId: job.jobId });
                await sleep(pollMs, signal);
            }
        }

        async function split(file, o) {
            o = o || {};
            var signal = o.signal, phase = o.onPhase || noop;
            var inf = o.info || lastInfo || await info();
            if (!inf || !inf.enabled) throw typed('disabled', 'lane closed');
            if (inf.login_required) throw typed('login_required', 'login required');
            var src = await durable(file, o.name);
            if (signal && signal.aborted) throw cancelled();

            var fd = new FormData();
            fd.append('source', src, o.name || src.name || 'song');
            fd.append('mode', o.mode || cfg.mode || inf.default_mode || '');
            if (purpose !== 'stem_split') fd.append('purpose', purpose);
            if (o.fallbackReason) { fd.append('fallback', '1'); fd.append('fallback_reason', o.fallbackReason); }
            if (o.acceptPaid) fd.append('accept_paid', '1');
            if (o.onlyIfOwned) fd.append('only_if_owned', '1');

            var t0 = Date.now();
            phase('upload', { loaded: 0, total: src.size || 0 });
            var r;
            for (var attempt = 1; ; attempt++) {
                r = await post(cfg.runUrl, fd, { signal: signal, uploadStallMs: cfg.uploadStallMs, answerMs: cfg.answerMs,
                    onUpload: function (l, t) { phase('upload', { loaded: l, total: t }); } });
                // a gateway answer decided nothing; if the first attempt DID reach the server, the
                // duplicate guard answers the second with 409 «already splitting» — no second charge
                if (!gateway(r.http, r.d) || attempt >= 3) break;
                phase('retry', { http: r.http, attempt: attempt });
                await sleep((cfg.retryMs || 3000) * attempt, signal);
            }
            var job = { jobId: null, statusUrl: null, cost: 0, free: false, freeLeft: null, reused: null, uploadMs: Date.now() - t0 };
            if (r.http === 202 && r.d.status_url) {
                job.jobId = r.d.job_id; job.statusUrl = r.d.status_url;
                job.cost = r.d.cost | 0; job.free = !!r.d.free; job.freeLeft = r.d.free_left != null ? r.d.free_left : null;
            } else if (r.http === 409 && r.d.error_kind === 'duplicate' && r.d.status_url) {
                // the same file by the same person: attach to the run in flight, or take the stems
                // it already has — never a second charge for the same separation
                job.jobId = r.d.job_id; job.statusUrl = r.d.status_url;
                job.reused = r.d.in_flight ? 'in_flight' : 'delivered';
            } else {
                throw refusal(r.http, r.d);
            }
            phase('submitted', job);
            var t1 = Date.now();
            var st = await poll(job, inf, signal, phase);
            job.waitMs = Date.now() - t1;
            job.stems = st.stems || [];
            job.status = st;
            return job;
        }

        /**
         * One stem, guarded by silence: no byte for stemStallMs → the request is dropped and the stem
         * asked again from the start (the server keeps it for days — a retry costs nothing), up to
         * STEM_TRIES times. A 410 / 4xx is an answer, not a stall, and is not retried.
         */
        async function fetchOne(s, signal, onBytes) {
            for (var attempt = 1; ; attempt++) {
                var c = child(signal), stalled = false, timer = null, got = 0, parts = [];
                var arm = function () { clearTimeout(timer); timer = setTimeout(function () { stalled = true; try { c.abort(); } catch (_) {} }, stemStallMs); };
                try {
                    arm();
                    var r = await fetch(s.url, { credentials: 'same-origin', cache: 'no-store', signal: c.signal });
                    if (!r.ok) {
                        if (gateway(r.status, null)) throw typed('network', 'stem ' + s.stem + ' http ' + r.status, { gateway: r.status });
                        throw typed(r.status === 410 ? 'expired' : 'download', 'stem ' + s.stem + ' http ' + r.status);
                    }
                    if (!r.body || !r.body.getReader) {
                        var whole = await r.arrayBuffer();
                        onBytes(whole.byteLength);
                        return whole;
                    }
                    var reader = r.body.getReader();
                    for (;;) {
                        var ch = await reader.read();
                        if (ch.done) break;
                        arm();
                        parts.push(ch.value); got += ch.value.byteLength; onBytes(ch.value.byteLength);
                    }
                    var buf = new Uint8Array(got), at = 0;
                    parts.forEach(function (p) { buf.set(p, at); at += p.byteLength; });
                    return buf.buffer;
                } catch (e) {
                    if (signal && signal.aborted) throw cancelled();
                    if (e && (e.kind === 'expired' || e.kind === 'download')) throw e;
                    onBytes(-got);                                   // what came of this attempt is thrown away
                    if (attempt >= STEM_TRIES) throw typed('network', stalled ? 'stem download stalled' : 'stem download failed', { stem: s.stem, stalled: stalled, tries: attempt, gateway: (e && e.data && e.data.gateway) || null });
                    if (e && e.data && e.data.gateway) await sleep((cfg.retryMs || 3000) * attempt, signal);
                } finally {
                    clearTimeout(timer);
                    c.release();
                }
            }
        }

        /** Download the named stems (all when `names` is empty), one at a time — a phone holds one FLAC in flight. */
        async function fetchStems(list, names, o) {
            o = o || {};
            var want = (list || []).filter(function (s) { return !names || !names.length || names.indexOf(s.stem) >= 0; });
            var total = want.reduce(function (a, s) { return a + (s.bytes || 0); }, 0), got = 0, out = {};
            for (var i = 0; i < want.length; i++) {
                if (o.signal && o.signal.aborted) throw cancelled();
                out[want[i].stem] = await fetchOne(want[i], o.signal, function (d) {
                    got += d;
                    if (o.onProgress) o.onProgress(got, Math.max(total, got));
                });
            }
            return out;
        }

        return { info: info, quote: quote, split: split, fetchStems: fetchStems, lastInfo: function () { return lastInfo; } };
    }

    root.StemSplitLane = { create: create };
})(typeof window !== 'undefined' ? window : self);
